/**
 * mandate-scope.ts - the Brokermandat's step 3 (register check before the broker contract) and
 * step 5 (Mandatsumfang) vocabulary, wire shapes and pure helpers. Shared by apps/api and apps/web.
 *
 * Every status word is a code here; the German words live in the label maps below and nowhere else.
 *
 * [Source: docs/product/p4-p6-slices/p5-S1-ausloesung-register-stub-mandatsumfang-dokument-design.md] (§2.2, §3.1)
 */

import type { LineOfBusiness } from './policy';

// ── Register check (step 3, manual attestation) ──────────────────────────────

export const MANDATE_REGISTER_CHECK_RESULT = {
  OHNE_BEFUND: 'ohne_befund',
  BEFUND: 'befund',
  UEBERSPRUNGEN: 'uebersprungen',
} as const;
export type MandateRegisterCheckResult =
  (typeof MANDATE_REGISTER_CHECK_RESULT)[keyof typeof MANDATE_REGISTER_CHECK_RESULT];
export const MANDATE_REGISTER_CHECK_RESULT_VALUES = Object.values(MANDATE_REGISTER_CHECK_RESULT) as MandateRegisterCheckResult[];

export const MANDATE_REGISTER_CHECK_SOURCE = {
  HANDELSREGISTER: 'handelsregister',
  SANKTIONSLISTE: 'sanktionsliste',
  PEP: 'pep',
} as const;
export type MandateRegisterCheckSource =
  (typeof MANDATE_REGISTER_CHECK_SOURCE)[keyof typeof MANDATE_REGISTER_CHECK_SOURCE];
export const MANDATE_REGISTER_CHECK_SOURCE_VALUES = Object.values(MANDATE_REGISTER_CHECK_SOURCE) as MandateRegisterCheckSource[];

export const MANDATE_REGISTER_CHECK_SKIP_REASON = {
  PRIVATKUNDE_OHNE_RISIKO: 'privatkunde_ohne_risiko',
  BESTEHENDER_KUNDE: 'bestehender_kunde',
  SONSTIGES: 'sonstiges',
} as const;
export type MandateRegisterCheckSkipReason =
  (typeof MANDATE_REGISTER_CHECK_SKIP_REASON)[keyof typeof MANDATE_REGISTER_CHECK_SKIP_REASON];
export const MANDATE_REGISTER_CHECK_SKIP_REASON_VALUES =
  Object.values(MANDATE_REGISTER_CHECK_SKIP_REASON) as MandateRegisterCheckSkipReason[];

export const MANDATE_REGISTER_CLEAR_REASON = {
  FALSCH_POSITIV: 'falsch_positiv',
  SANKTION_AUFGEHOBEN: 'sanktion_aufgehoben',
  NEU_GEPRUEFT: 'neu_geprueft',
  SONSTIGES: 'sonstiges',
} as const;
export type MandateRegisterClearReason =
  (typeof MANDATE_REGISTER_CLEAR_REASON)[keyof typeof MANDATE_REGISTER_CLEAR_REASON];
export const MANDATE_REGISTER_CLEAR_REASON_VALUES = Object.values(MANDATE_REGISTER_CLEAR_REASON) as MandateRegisterClearReason[];

/** The written reason of a clear: required, 3..2000 characters (Valerio 22.10.). */
export const MANDATE_REGISTER_CLEAR_TEXT = { min: 3, max: 2000 } as const;
/** The broker's note on a check (a Befund's detail): optional, at most 2000 characters. */
export const MANDATE_REGISTER_NOTE_MAX = 2000;

export const MANDATE_REGISTER_CHECK_MODE = { MANUAL: 'manual', SERVICE: 'service' } as const;
export type MandateRegisterCheckMode = (typeof MANDATE_REGISTER_CHECK_MODE)[keyof typeof MANDATE_REGISTER_CHECK_MODE];

/** Why a broker mandate is held before its document: no check yet, or an uncleared Befund on the customer. */
export type MandateRegisterLockReason = 'missing' | 'befund';

/** The title of the Compliance task a Befund opens (process mandate_signing). */
export const REGISTER_BEFUND_TASK_TITLE = 'Register-Befund Brokermandat prüfen' as const;
/** The outcomes written onto that task's meta when it is closed or its Vorgang is aborted. */
export const REGISTER_BEFUND_TASK_OUTCOME = { FREIGEGEBEN: 'freigegeben' } as const;

export const REGISTER_CHECK_RESULT_LABEL: Record<MandateRegisterCheckResult, string> = {
  ohne_befund: 'ohne Befund',
  befund: 'Befund',
  uebersprungen: 'übersprungen',
};

export const REGISTER_CHECK_SOURCE_LABEL: Record<MandateRegisterCheckSource, string> = {
  handelsregister: 'Handelsregister',
  sanktionsliste: 'Sanktionsliste',
  pep: 'PEP',
};

export const REGISTER_CHECK_SKIP_REASON_LABEL: Record<MandateRegisterCheckSkipReason, string> = {
  privatkunde_ohne_risiko: 'Privatkunde ohne Risiko',
  bestehender_kunde: 'Bestehender Kunde, bereits geprüft',
  sonstiges: 'Sonstiges',
};

export const REGISTER_CLEAR_REASON_LABEL: Record<MandateRegisterClearReason, string> = {
  falsch_positiv: 'Falsch positiv',
  sanktion_aufgehoben: 'Sanktion aufgehoben',
  neu_geprueft: 'Neu geprüft, kein Befund',
  sonstiges: 'Sonstiges',
};

/** One register check as the api returns it. Never carries the written clear reason or the note. */
export interface RegisterCheckDto {
  id: string;
  mandateId: string | null;
  mode: MandateRegisterCheckMode;
  result: MandateRegisterCheckResult;
  sources: MandateRegisterCheckSource[];
  befundSources: MandateRegisterCheckSource[];
  skipReasonCode: MandateRegisterCheckSkipReason | null;
  registerSource: 'excerpt_pdf' | 'zefix_replay' | null;
  registerFetchedAt: string | null;
  checkedAt: string;
  checkedByAppUserId: string | null;
  complianceTaskId: string | null;
  clearedAt: string | null;
  clearedByAppUserId: string | null;
}

/** GET /mandates/:id/register-check: the latest check of the mandate and the customer's lock. */
export interface RegisterCheckStateDto {
  check: RegisterCheckDto | null;
  registerStatus: 'verifiziert' | 'nicht_verifiziert' | 'nicht_zutreffend';
  registerSource: 'excerpt_pdf' | 'zefix_replay' | null;
  registerFetchedAt: string | null;
  locked: boolean;
  reason: MandateRegisterLockReason | null;
  /** The customer's uncleared Befund (may come from an earlier, cancelled draft); details only for `tenant.manage`. */
  befund: RegisterCheckDto | null;
}

/** The permission that clears a Register-Befund and sees its details. */
export const REGISTER_BEFUND_ADMIN_PERMISSION = 'tenant.manage' as const;

/** POST /mandates/:id/register-check body. */
export interface RecordRegisterCheckBody {
  result: MandateRegisterCheckResult;
  sources: MandateRegisterCheckSource[];
  befundSources?: MandateRegisterCheckSource[];
  skipReasonCode?: MandateRegisterCheckSkipReason;
  notes?: string;
}

/** POST /customers/:t/:id/register-befund/:checkId/freigeben body. */
export interface ClearRegisterBefundBody {
  reasonCode: MandateRegisterClearReason;
  reason: string;
}

/** GET /customers/:t/:id/register-befund: the red flag on the customer page. */
export interface CustomerRegisterBefundDto {
  /**
   * The open Befund. Without `tenant.manage` it carries only its id and dates: the sources, the
   * register snapshot and the people are blanked (a PEP or sanctions hit is sensitive).
   */
  befund: RegisterCheckDto | null;
  /** Whether the Befund's details (sources, recorder) are included for this caller. */
  detailsVisible: boolean;
  /** The draft the Befund was recorded on, while it still exists (the «Vorgang abbrechen» target). */
  openDraftMandateId: string | null;
}

// ── Mandatsumfang (step 5) ───────────────────────────────────────────────────

export const MANDATE_SCOPE_SOURCE = {
  BESTAND: 'bestand',
  RELATIONSHIP: 'relationship',
  RUECKLAUF: 'ruecklauf',
  MANUAL: 'manual',
} as const;
export type MandateScopeSource = (typeof MANDATE_SCOPE_SOURCE)[keyof typeof MANDATE_SCOPE_SOURCE];

export const MANDATE_SCOPE_EXCLUDE_REASON = {
  BROKER: 'broker',
  KUNDE_GESTRICHEN: 'kunde_gestrichen',
  VN_ABWEICHEND: 'vn_abweichend',
  POLICE_INAKTIV: 'police_inaktiv',
  ZUSAMMENGEFUEHRT: 'zusammengefuehrt',
} as const;
export type MandateScopeExcludeReason =
  (typeof MANDATE_SCOPE_EXCLUDE_REASON)[keyof typeof MANDATE_SCOPE_EXCLUDE_REASON];
/** The reasons a broker may pick in the wizard (zusammengefuehrt is written by the system only). */
export const MANDATE_SCOPE_EXCLUDE_REASON_INPUT: MandateScopeExcludeReason[] = [
  MANDATE_SCOPE_EXCLUDE_REASON.BROKER,
  MANDATE_SCOPE_EXCLUDE_REASON.KUNDE_GESTRICHEN,
  MANDATE_SCOPE_EXCLUDE_REASON.VN_ABWEICHEND,
  MANDATE_SCOPE_EXCLUDE_REASON.POLICE_INAKTIV,
];

export const SCOPE_EXCLUDE_REASON_LABEL: Record<MandateScopeExcludeReason, string> = {
  broker: 'vom Broker ausgeschlossen',
  kunde_gestrichen: 'vom Kunden gestrichen',
  vn_abweichend: 'Versicherungsnehmer abweichend',
  police_inaktiv: 'Police inaktiv',
  zusammengefuehrt: 'mit der Police zusammengeführt',
};

/** At most this many items per PUT scope. */
export const MANDATE_SCOPE_MAX_ITEMS = 200;
/** The prefix of a placeholder policy number the ingest writes; never printed in the annex. */
export const DRAFT_POLICY_NUMBER_PREFIX = 'ENTWURF-';

/** A manual addition in the wizard (it also creates an F3 row with source 'manual'). */
export interface ScopeManualInput {
  carrierUid?: string | null;
  carrierName: string;
  lineOfBusiness?: LineOfBusiness | null;
  sparteText?: string | null;
  policyNumber?: string | null;
}

/** One item of PUT /mandates/:id/scope. A policy item may carry its F3 row as an annotation. */
export interface ScopeItemInput {
  policyId?: string | null;
  relationshipId?: string | null;
  manual?: ScopeManualInput;
  included: boolean;
  excludedReason?: MandateScopeExcludeReason | null;
}

export interface MandateScopeItemDto {
  id: string;
  policyId: string | null;
  relationshipId: string | null;
  carrierUid: string | null;
  carrierUidNormalized: string | null;
  carrierName: string | null;
  policyNumber: string | null;
  lineOfBusiness: LineOfBusiness | null;
  sparteText: string | null;
  source: MandateScopeSource;
  included: boolean;
  excludedReason: MandateScopeExcludeReason | null;
  sortOrder: number;
  /** Proposal only: the policy is a draft (Police in Abklärung); its number is not printed. */
  draftPolicy?: boolean;
}

export interface ScopeCarrierGroup {
  carrierKey: string;
  carrierUid: string | null;
  carrierName: string | null;
  items: MandateScopeItemDto[];
  includedCount: number;
}

export interface ScopeProposalDto {
  groups: ScopeCarrierGroup[];
}

export interface ScopeDto {
  items: MandateScopeItemDto[];
  includedCount: number;
  groups: ScopeCarrierGroup[];
  documentDiscarded: boolean;
}

export interface SetScopeBody {
  items: ScopeItemInput[];
}

/** One line of the Policenliste annex: Versicherer · Sparte · Policennummer. */
export interface PolicenlisteRow {
  carrierName: string;
  sparte: string | null;
  policyNumber: string | null;
}

/** Error codes of the Brokermandat steps 3 and 5 (ConflictException / BadRequestException `code`). */
export const MANDATE_BROKER_ERROR = {
  REGISTER_BEFUND: 'mandate_register_befund',
  REGISTER_CHECK_MISSING: 'mandate_register_check_missing',
  SCOPE_EMPTY: 'mandate_scope_empty',
  SCOPE_FROZEN: 'mandate_scope_frozen',
  SCOPE_EXISTS: 'mandate_scope_exists',
  NOT_BROKER: 'mandate_not_broker',
  NOT_SIGNED: 'mandate_not_signed',
  NOT_SENT: 'mandate_not_sent',
  NOT_DRAFT: 'mandate_not_draft',
  CHECK_NOT_OPEN: 'register_check_not_open',
} as const;

// ── Pure helpers ─────────────────────────────────────────────────────────────

/**
 * upper(regexp_replace(uid, '[^A-Za-z0-9]', '', 'g')): the TS twin of the SQL expression the
 * open-notification index and the versicherer lookup use. Pure.
 */
export function normalizeCarrierUid(uid: string): string {
  return (uid ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** The grouping key of a scope item: the normalised UID, else the lower-cased, trimmed name. */
export function scopeCarrierKey(item: Pick<MandateScopeItemDto, 'carrierUid' | 'carrierName'>): string {
  const uid = item.carrierUid !== null ? normalizeCarrierUid(item.carrierUid) : '';
  if (uid !== '') return uid;
  const name = (item.carrierName ?? '').trim().toLowerCase();
  if (name !== '') return `name:${name}`;
  throw new Error('A scope item needs a carrier UID or a carrier name.');
}

function compareText(a: string | null, b: string | null): number {
  return (a ?? '').localeCompare(b ?? '', 'de-CH', { sensitivity: 'base', numeric: true });
}

/**
 * Group scope items per carrier (one Meldung per carrier later): by normalised UID, unresolved
 * carriers by name. Groups by carrier name ascending, items by policy number. Pure; throws on an
 * item with neither a UID nor a name.
 */
export function groupScopeByCarrier(items: MandateScopeItemDto[]): ScopeCarrierGroup[] {
  const groups = new Map<string, ScopeCarrierGroup>();
  for (const item of items) {
    const key = scopeCarrierKey(item);
    let group = groups.get(key);
    if (group === undefined) {
      group = {
        carrierKey: key,
        carrierUid: item.carrierUid,
        carrierName: item.carrierName !== null ? item.carrierName.trim() : null,
        items: [],
        includedCount: 0,
      };
      groups.set(key, group);
    }
    if (group.carrierName === null && item.carrierName !== null) group.carrierName = item.carrierName.trim();
    if (group.carrierUid === null && item.carrierUid !== null) group.carrierUid = item.carrierUid;
    group.items.push(item);
    if (item.included) group.includedCount += 1;
  }
  const out = [...groups.values()];
  for (const g of out) g.items.sort((a, b) => compareText(a.policyNumber, b.policyNumber) || a.sortOrder - b.sortOrder);
  out.sort((a, b) => compareText(a.carrierName ?? a.carrierKey, b.carrierName ?? b.carrierKey));
  return out;
}

/** The annex label of an item: «AXA · Police Nr. A1» or «AXA · ohne Policennummer». */
export function scopeItemLabel(item: Pick<MandateScopeItemDto, 'carrierName' | 'carrierUid' | 'policyNumber'>): string {
  const carrier = item.carrierName ?? item.carrierUid ?? '';
  return item.policyNumber ? `${carrier} · Police Nr. ${item.policyNumber}` : `${carrier} · ohne Policennummer`;
}
